import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { mergeArray } from "common/hooks/mergeArray";

export const useSingleSelect = ({ value, fixedValue, onChange }) => {
  const [internalValue, setInternalValue] = useState(value || fixedValue || "");

  const setValue = (val) => {
    if (val === fixedValue) return;
    setInternalValue(val);
    onChange?.(val);
  };

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [internalValue, value]);

  return {
    currentValue: internalValue,
    setValue,
  };
};

export const extractFixed = (children) => {
  return React.Children.toArray(children)
    .map((child) => child.props)
    .filter((props) => props.fixed)
    .map((props) => props.value);
};

export const useInitFixed = (setValue, onValueChange, fixedValues) => {
  const didInitRef = useRef(false);

  useEffect(() => {
    if (fixedValues.length > 0 && !didInitRef.current) {
      setValue(fixedValues);
      onValueChange(fixedValues);
      didInitRef.current = true;
    }
  }, [fixedValues, onValueChange, setValue]);
};

export const useMultiSelect = ({
  value,
  onChange,
  fixedValues = [],
  defaultValue = [],
}) => {
  const isControlled = Array.isArray(value) && typeof onChange === "function";
  const [internalValue, setInternalValue] = useState(() =>
    mergeArray(defaultValue, fixedValues)
  );

  // useCallback 제거, 직접 계산
  const baseValue = isControlled ? value : internalValue;

  const currentValue = useMemo(
    () => mergeArray(baseValue, fixedValues),
    [JSON.stringify(baseValue), JSON.stringify(fixedValues)]
  );

  useEffect(() => {
    if (!isControlled && value && Array.isArray(value)) {
      setInternalValue(mergeArray(value, fixedValues));
    }
  }, [isControlled, JSON.stringify(fixedValues), JSON.stringify(value)]);

  const currentSet = useMemo(
    () => new Set(currentValue),
    [JSON.stringify(currentValue)]
  );

  const setValue = useCallback(
    (next) => {
      const merged = mergeArray(
        typeof next === "function" ? next(baseValue) : next,
        fixedValues
      );
      if (isControlled) {
        onChange(merged);
      } else {
        setInternalValue(merged);
      }
    },
    [
      isControlled,
      JSON.stringify(baseValue),
      JSON.stringify(fixedValues),
      onChange,
    ]
  );

  const toggle = useCallback(
    (val) => {
      if (fixedValues.includes(val)) return;
      setValue((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
      );
    },
    [JSON.stringify(fixedValues), setValue]
  );

  return {
    currentValue,
    currentSet,
    toggle,
    setValue,
  };
};
